import { Schema } from "mongoose"
import { z } from "zod"

import type { AclPolicy } from "../acl"


export const ZRBUploadSessionStatus = z.enum(["uploading", "assembling", "done", "error"])

export const ZRBUploadSession = z.object({
  _id: z.string(),
  userId: z.string().optional(),
  ownerKeyHash: z.string().optional(),
  filename: z.string(),
  mimeType: z.string(),
  totalSize: z.number().int().min(0),
  chunkSize: z.number().int().min(1),
  chunksTotal: z.number().int().min(1),
  status: ZRBUploadSessionStatus,
  createdAt: z.date(),
  expiresAt: z.date(),
  fileId: z.string().optional(),
  isPublic: z.boolean().optional(),
  error: z.string().optional(),
})

export type IRBUploadSession = z.infer<typeof ZRBUploadSession>

export const RBUploadSessionSchema: Schema = new Schema(
  {
    _id: { type: String, required: true },
    userId: { type: String, required: false, index: true },
    ownerKeyHash: { type: String, required: false },
    filename: { type: String, required: true },
    mimeType: { type: String, required: true },
    totalSize: { type: Number, required: true },
    chunkSize: { type: Number, required: true },
    chunksTotal: { type: Number, required: true },
    status: { type: String, required: true, enum: ZRBUploadSessionStatus.options },
    createdAt: { type: Date, required: true, default: Date.now },
    expiresAt: { type: Date, required: true },
    fileId: { type: String, required: false },
    isPublic: { type: Boolean, required: false },
    error: { type: String, required: false },
  },
  {
    versionKey: false,
  },
)

RBUploadSessionSchema.index({ expiresAt: 1 }, { expireAfterSeconds: 0 })

export const RBUploadSessionPolicy: AclPolicy = {
  subject: "RBUploadSession",
  define: (builder, ctx) => {
    builder.can("create", "RBUploadSession")

    if (ctx.userId) {
      builder.can("read", "RBUploadSession", { userId: ctx.userId })
      builder.can("update", "RBUploadSession", { userId: ctx.userId })
      builder.can("delete", "RBUploadSession", { userId: ctx.userId })
    }

    const uploadKeyHash = typeof ctx.claims?.uploadKeyHash === "string" ? ctx.claims.uploadKeyHash.trim() : ""
    if (uploadKeyHash) {
      builder.can("read", "RBUploadSession", { ownerKeyHash: uploadKeyHash })
      builder.can("update", "RBUploadSession", { ownerKeyHash: uploadKeyHash })
      builder.can("delete", "RBUploadSession", { ownerKeyHash: uploadKeyHash })
    }
  },
}
